import React, { useEffect, useState } from 'react'
import moment from 'moment'
import { useGatherAsk } from '../../../../marketing-v2/features/social/data/askData'
import { postTheme } from '../../../../marketing-v2/features/social/render/render'
import { POST_TYPES } from '../../../../marketing-v2/features/social/data/postTypes'
import { savePost, loadPosts } from '../../../../marketing-v2/features/social/data/store'
import { townOf } from '../../../../marketing-v2/features/social/data/captions'
import Preview from '../../../../marketing-v2/features/social/components/Preview'
import '../../../../marketing-v2/features/social/Social.css'
import { hapticTick, hapticSuccess } from '../../../../../_utils/haptics'
import { buildPostFrom } from '../makeThings'
import { useBrand, useRecall } from '../useBeu'
import { Tick, Photo, Warn } from './icons'
import { useSaver, useEditor } from '../saveBus'
import { briefFor, askBailey, applyEdit } from '../../../../marketing-v2/features/social/data/askAI'

export const SOCIAL_PATH = '/marketing-social'

// "Planned for Fri 2 Oct at 6pm"
export const plannedSaid = (iso) => {
  const m = moment(iso)
  return m.isValid() ? `${m.format('ddd D MMM')} at ${m.format('h:mma').replace(':00', '')}` : ''
}
const kindSaid = (post) => `${(POST_TYPES[post.type] || {}).label || 'Social'} ${post.format === 'story' ? 'Story' : 'post'}`
// What Bailey used, without the "Made a … post" line the title already says
export const factsSaid = (changes) => (changes || []).filter(c => !/^Made /.test(c)).slice(0, 3)

// A change asked for in the chat, made to this post the way the post studio's
// own "Ask Bailey" makes it (one fast call, checked by applyEdit: only real
// fields, a client's review never reworded). { post, changes } or null.
export const editByAsking = async (post, text, brand) => {
  const now = new Date()
  const r = await askBailey({ brief: briefFor(post, { text, kit: brand.kit, trade: brand.market, now }), text, timeout: 30000 })
  if (!r || !r.ok) return null
  const res = applyEdit(post, r.edit, { kit: brand.kit, trade: brand.market, now })
  return res && res.changes && res.changes.length ? { post: res.post, changes: res.changes } : null
}

// Make a post from a block, once the salon's brand is known (so it wears it)
export const useMadePost = (block, key, skip) => {
  const brand = useBrand()
  const gather = useGatherAsk()
  const [made, setMade] = useState(null)
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    if (skip || !brand.ready) return undefined
    let alive = true
    buildPostFrom(block, {
      gather,
      kit: brand.kit,
      trade: brand.market,
      salonName: brand.salonName,
      town: townOf(brand.salon && brand.salon.formattedAddress),
    })
      .then((r) => { if (alive) setMade(r) })
      .catch(() => { if (alive) setFailed(true) })
    return () => { alive = false }
  }, [key, skip, brand.ready]) // eslint-disable-line react-hooks/exhaustive-deps
  return { brand, made, failed }
}

// A social post Bailey made: drawn with Social's own designs and the salon's
// own facts, saved to Social or opened in the studio only when tapped
const PostWidget = ({ block, id, compact, go, latest = false }) => {
  const [memo, setMemo] = useRecall(id)
  const [, bump] = useState(0)
  const saved = memo && memo.postId ? loadPosts().find(p => p.id === memo.postId) || null : null
  const { brand, made, failed } = useMadePost(block, id, !!saved)
  // Changed by asking in the chat, before it was saved
  const post = saved || (memo && memo.edited) || (made && made.post)

  const keep = () => {
    if (saved) return saved
    const s = savePost({ ...post, status: post.plannedAt ? 'planned' : 'draft' })
    setMemo({ postId: s.id })
    return s
  }
  const onSave = () => { hapticSuccess(); keep() }
  // "Less formal", "make it pink", "move it to Friday" in the chat: this post
  // changes in place (saved already: the saved one, kept saved)
  useEditor(id, latest && !!post, async (text) => {
    const res = await editByAsking(post, text, brand)
    if (!res) return null
    if (saved) { savePost({ ...res.post, id: saved.id, status: res.post.plannedAt ? 'planned' : (saved.status === 'posted' ? 'posted' : 'draft') }); bump(n => n + 1) } else setMemo({ ...(memo || {}), edited: res.post })
    return `the ${(POST_TYPES[res.post.type] || {}).label || 'social'} ${res.post.format === 'story' ? 'Story' : 'post'}: ${res.changes.slice(0, 3).join(', ').toLowerCase()}`
  })
  // "Yes" in the chat saves it, exactly as the Save / Plan button does
  useSaver(id, latest && !saved && !!post, () => {
    const s = keep()
    const kind = s.format === 'story' ? 'Story' : 'post'
    return s.plannedAt ? `1 ${kind} planned in Social for ${plannedSaid(s.plannedAt)}` : `1 ${kind} saved as a draft in Social`
  })
  const onOpen = () => { hapticTick(); const s = keep(); go(SOCIAL_PATH, { openPostId: s.id }) }

  if (failed) {
    return (
      <div className="bw-card" role="group" aria-label="Social post">
        <div className="bw-head"><span className="bw-kind"><Photo />Social post</span></div>
        <p className="bw-line">This post couldn’t be made just now. Social can make it from the same words.</p>
        <div className="bw-actions"><button type="button" className="bw-btn is-primary" onClick={() => go(SOCIAL_PATH, { create: true })}>Open Social</button></div>
      </div>
    )
  }
  if (!post) {
    return (
      <div className="bw-card is-loading" role="status" aria-label="Making the post">
        <div className="bw-head"><span className="bw-kind"><Photo />Social post</span></div>
        <div className="bw-body">
          <span className="bw-art bw-shimmer" aria-hidden />
          <div className="bw-text">
            <p className="bw-line">Making it with your diary, reviews and brand</p>
            <span className="bw-bar bw-shimmer" aria-hidden />
            <span className="bw-bar is-short bw-shimmer" aria-hidden />
          </div>
        </div>
      </div>
    )
  }
  const facts = saved ? [] : factsSaid(made && made.changes)
  const needs = saved ? [] : ((made && made.needs) || []).slice(0, 2)
  const planned = post.plannedAt ? plannedSaid(post.plannedAt) : ''
  return (
    <div className={`bw-card${saved ? ' is-done' : ''}`} role="group" aria-label={block.title || kindSaid(post)}>
      <div className="bw-head">
        <span className="bw-kind"><Photo />Social post</span>
        {saved && <span className="bw-status is-done"><Tick size={12} />{saved.status === 'planned' ? `Planned for ${plannedSaid(saved.plannedAt)}` : saved.status === 'posted' ? 'Posted' : 'Saved in Social'}</span>}
      </div>
      <div className="bw-body">
        <button type="button" className={`bw-art${post.format === 'story' ? ' is-story' : ''}`} onClick={onOpen} aria-label="Open it in the studio">
          <Preview post={post} theme={postTheme(post, { kit: brand.kit })} format={post.format} />
        </button>
        <div className="bw-text">
          <p className="bw-title">{block.title || kindSaid(post)}</p>
          {facts.length > 0 && (
            <ul className="bw-facts">
              {facts.map(f => <li key={f}><Tick />{f}</li>)}
            </ul>
          )}
          {needs.map(n => <p key={n.text} className="bw-need"><Warn size={14} />{n.text}</p>)}
          {!compact && made && made.captionText && <p className="bw-caption">{made.captionText}</p>}
        </div>
      </div>
      <div className="bw-actions">
        {!saved && <button type="button" className="bw-btn is-secondary" onClick={onSave}>{planned ? `Plan for ${planned}` : 'Save as a draft'}</button>}
        <button type="button" className="bw-btn is-primary" onClick={onOpen}>Open in the studio</button>
      </div>
    </div>
  )
}

export default PostWidget
